import React, { useCallback, useState } from 'react';
import { Button, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useMeetingStaffContext } from 'components/meetings/MeetingPage/MeetingStaffView/MeetingStaffContext';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import { ActivityType, IPCheckType, QuestionResponse } from 'store/enums/BallotReviewTypes';
import { meetingSessionToActivity } from 'util/meeting';

export const displayCompletionConditionStatement = 'display-completion-condition';

const IPCheckCompletionSection: React.FunctionComponent = () => {
  const { meetingId, meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();
  const [conditionCheck, setConditionCheck] = useState<QuestionResponse | undefined>(
    meetingSession?.currentIpCheckSession?.completionConditionCheck
  );

  const saveCompletionProgress = useCallback(
    async (value?: QuestionResponse) => {
      try {
        await MeetingService.saveMeetingSession({
          ...meetingSession,
          meetingId,
          currentIpCheckSession: {
            ...meetingSession?.currentIpCheckSession,
            type: IPCheckType.Affirmative,
            displayTrademark: undefined,
            hasTrademark: undefined,
            isTrademarkJustified: undefined,
            completionConditionCheck: value,
          },
        });
      } catch (error) {
        console.error('Error saving completion condition check progress:', error);
      }
    },
    [meetingId, meetingSession]
  );

  const handleDisplay = useCallback(async () => {
    try {
      await MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentIpCheckSession: {
          ...meetingSession?.currentIpCheckSession,
          type: IPCheckType.Affirmative,
          statement: displayCompletionConditionStatement,
          displayTrademark: undefined,
          hasTrademark: undefined,
          isTrademarkJustified: undefined,
          displayCompletionCondition: true,
          completionConditionCheck: conditionCheck,
        },
      });
    } catch (error) {
      console.error('Error displaying completion condition check:', error);
    }
  }, [conditionCheck, meetingId, meetingSession]);

  const handleSave = useCallback(async () => {
    logActivity(
      {
        ...meetingSessionToActivity(meetingSession),
        type: ActivityType.IpCheck,
        motion: meetingSession?.currentMotion,
        votingSession: meetingSession?.currentVotingSession,
        ipCheckSession: {
          type: IPCheckType.Affirmative,
          completionConditionCheck: conditionCheck,
        },
      },
      'Saved completion condition check'
    );
  }, [conditionCheck, logActivity, meetingSession]);

  const handleClear = useCallback(() => {
    setConditionCheck(undefined);
    void MeetingService.saveMeetingSession({
      ...meetingSession,
      meetingId,
      currentIpCheckSession: {
        ...meetingSession?.currentIpCheckSession,
        type: IPCheckType.Affirmative,
        statement: undefined,
        displayCompletionCondition: undefined,
        completionConditionCheck: undefined,
      },
    });
  }, [meetingId, meetingSession]);

  return (
    <GridContainer spacing={1}>
      <GridItem>
        <Typography variant="h6">IP check completion condition check</Typography>
      </GridItem>
      <GridItem>
        <Typography>{`The co-chair checks if any Patented Technologies first become known to the TC Chapter
          on or after the day of the issuance of this Letter Ballot? i.e., (Number of patented technologies
          first became known to the TC Chapter on or after the day of the issuance of this Letter Ballot) >
          0 in (Total numbers of Patented Technologies to be dealt with)`}</Typography>
      </GridItem>
      <GridItem>
        <RadioGroup
          name="ip-condition-check-radio-group"
          value={conditionCheck ?? ''}
          onChange={(event) => {
            const value = event.target.value as QuestionResponse;
            if (value === conditionCheck) return;

            setConditionCheck(value);
            void saveCompletionProgress(value);
          }}
        >
          <FormControlLabel value={QuestionResponse.Yes} control={<Radio />} label={QuestionResponse.Yes} />
          <FormControlLabel value={QuestionResponse.No} control={<Radio />} label={QuestionResponse.No} />
        </RadioGroup>
      </GridItem>
      {conditionCheck === QuestionResponse.Yes && (
        <GridItem>
          <Typography>
            {`Sections 'Assessment of disclosed patented technologies' and 'LOA status check of PMPT' shall be completed
            and recorded for such patented technologies at next scheduled meeting of the TC Chapter. Until then, the TC
            Chapter shall NOT go to Action (making motion to pass/fail this Document) (see Regulations ¶ 16.4.1.2) Until
            then this Letter Ballot Review is on hold.`}
          </Typography>
        </GridItem>
      )}
      {conditionCheck === QuestionResponse.No && (
        <GridItem>
          <Typography>Go To Action</Typography>
        </GridItem>
      )}
      <GridItem>
        <Button variant="contained" color="secondary" sx={{ mr: 1 }} onClick={handleDisplay}>
          Display
        </Button>
        <Button variant="contained" color="secondary" disabled={!conditionCheck} onClick={handleSave}>
          Save
        </Button>
        <Button variant="contained" color="error" sx={{ ml: 1 }} onClick={handleClear}>
          Clear
        </Button>
      </GridItem>
    </GridContainer>
  );
};

export default IPCheckCompletionSection;
